import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useApp } from 'contexts/app';
import ProductService from 'services/product';
import ProductVariantService from 'services/product_variant';
import PricingService from 'services/pricing';

const service = new ProductService();
const productVariantService = new ProductVariantService();
const pricingService = new PricingService();

const normalizeAutoRule = (autoRule) => {
  const type = autoRule?.type || autoRule?.discount_type || 'percent';
  const value = Number(autoRule?.value ?? autoRule?.discount_value ?? 0) || 0;
  return {
    type: type === 'fixed_amount' ? 'fixed_amount' : 'percent',
    value: Math.max(0, value),
  };
};

const applyAutoRuleToPrice = (price, autoRule) => {
  const p = Number(price) || 0;
  if (autoRule?.type === 'fixed_amount') {
    return Math.max(0, p - (Number(autoRule?.value) || 0));
  }
  const pct = Math.min(100, Math.max(0, Number(autoRule?.value) || 0));
  return Math.round(p * (1 - pct / 100));
};

const cloneVariants = (variants = []) => {
  return (variants || []).map((v) => ({
    ...v,
    price_list: [...(v?.price_list || [])],
  }));
};

const isProductInPriceTable = (product = {}, table = {}) => {
  const applyTo = table?.apply_to || 'all';
  if (applyTo === 'all') return true;

  if (applyTo === 'manual') {
    return (table?.product_ids || []).includes(product?.id);
  }

  if (applyTo === 'tag') {
    const productTags = product?.tags || [];
    const tableTags = table?.tag_ids || [];
    return tableTags.some((tagId) => productTags.includes(tagId));
  }

  if (applyTo === 'category') {
    const catId = product?.category_id || product?.product_category_id;
    return (table?.category_ids || []).includes(catId);
  }

  return false;
};

const cloneVariantList = (variants = []) => {
  return (variants || []).map((v) => ({
    ...v,
    price_list: [...(v?.price_list || [])],
  }));
};

const useComponent = (config = {}) => {
  const { id: routeId } = useParams();
  const id = config?.id ?? routeId;
  const draftMode = !!config?.draftMode;
  const navigate = useNavigate();
  const { showNotify, showLoading } = useApp();

  const [product, setProduct] = useState({});
  const [productVariants, setProductVariants] = useState([]);
  const [baseVariants, setBaseVariants] = useState([]);
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [applicableTables, setApplicableTables] = useState([]);
  const [selectedSourceKey, setSelectedSourceKey] = useState('base');

  useEffect(() => {
    if (!draftMode && id) {
      loadData();
    }
  }, [id, draftMode]);

  useEffect(() => {
    if (!draftMode) {
      return;
    }

    const nextDraftVariants = cloneVariantList(config?.draftVariants || []);
    setProduct({});
    setBaseVariants(nextDraftVariants);
    setProductVariants(nextDraftVariants);
    setPricingEnabled(false);
    setApplicableTables([]);
    setSelectedSourceKey('base');
  }, [draftMode, config?.draftVariants]);

  const loadData = async () => {
    setProduct({});
    setApplicableTables([]);
    setSelectedSourceKey('base');

    const [item, setting] = await Promise.all([
      service.get(id),
      pricingService.getSetting().catch(() => ({ enabled: false })),
    ]);

    const nextProduct = item || {};
    const nextBaseVariants = cloneVariants(nextProduct?.variants || []);
    setProduct(nextProduct);
    setBaseVariants(nextBaseVariants);
    setProductVariants(nextBaseVariants);

    const enabled = !!setting?.enabled;
    setPricingEnabled(enabled);

    if (enabled) {
      const tables = await pricingService.list().catch(() => []);
      const matchedTables = (tables || []).filter((table) => isProductInPriceTable(nextProduct, table));
      setApplicableTables(matchedTables);
    }
  };

  const selectedTable = selectedSourceKey.startsWith('table:')
    ? applicableTables.find((t) => String(t.id) === String(selectedSourceKey.replace('table:', '')))
    : null;

  const isTableSource = !!selectedTable;
  const isTableManualMode = isTableSource && selectedTable?.mode === 'manual';
  const isTableAutoMode = isTableSource && selectedTable?.mode === 'auto';

  const sourceOptions = [
    { label: 'Giá sản phẩm gốc', value: 'base' },
    ...(applicableTables || []).map((table) => ({
      label: `${table?.name || 'Bảng giá'} (${table?.mode === 'auto' ? 'Tự động' : 'Cố định'})`,
      value: `table:${table.id}`,
    })),
  ];

  useEffect(() => {
    const syncBySource = async () => {
      if (!isTableSource) {
        setProductVariants(cloneVariants(baseVariants));
        return;
      }

      if (isTableAutoMode) {
        const rule = normalizeAutoRule(selectedTable?.auto_rule);
        const computed = cloneVariants(baseVariants).map((variant) => ({
          ...variant,
          original_price: applyAutoRuleToPrice(variant?.original_price ?? 0, rule),
          price_list: (variant?.price_list || []).map((tier) => ({
            ...tier,
            price: applyAutoRuleToPrice(tier?.price ?? 0, rule),
          })),
        }));
        setProductVariants(computed);
        return;
      }

      if (isTableManualMode) {
        const items = await pricingService.getPriceItems(selectedTable?.id).catch(() => []);
        const itemMap = (items || []).reduce((acc, item) => {
          acc[item?.variant_id] = item;
          return acc;
        }, {});

        const merged = cloneVariants(baseVariants).map((variant) => {
          const mapped = itemMap[variant?.id];
          if (!mapped) return variant;
          return {
            ...variant,
            original_price: Number(mapped?.original_price) || variant?.original_price || 0,
            price_list: (mapped?.price_list && mapped?.price_list.length > 0)
              ? [...mapped.price_list]
              : [...(variant?.price_list || [])],
          };
        });
        setProductVariants(merged);
      }
    };

    syncBySource();
  }, [selectedSourceKey, baseVariants, selectedTable?.id, selectedTable?.mode]);

  const onUpdateProductVariantPriceList = async () => {
    if (draftMode) {
      config?.onDraftVariantsChange?.(cloneVariantList(productVariants));
      showNotify('Đã cập nhật bảng giá tạm thời');
      return;
    }

    showLoading(true);
    try {
      if (!isTableSource) {
        await productVariantService.updatePriceList({ variants: productVariants });
      } else if (isTableManualMode) {
        const items = (productVariants || []).map((variant) => ({
          variant_id: variant?.id,
          product_id: variant?.product_id,
          original_price: variant?.original_price ?? 0,
          price_list: variant?.price_list ?? [],
        }));
        await pricingService.savePriceItems({ id: selectedTable?.id, items });
        // Reload saved items to verify changes persisted
        const savedItems = await pricingService.getPriceItems(selectedTable?.id).catch(() => []);
        const itemMap = (savedItems || []).reduce((acc, item) => {
          acc[item?.variant_id] = item;
          return acc;
        }, {});
        const reloadedVariants = cloneVariants(baseVariants).map((variant) => {
          const mapped = itemMap[variant?.id];
          if (!mapped) return variant;
          return {
            ...variant,
            original_price: Number(mapped?.original_price) || variant?.original_price || 0,
            price_list: (mapped?.price_list && mapped?.price_list.length > 0)
              ? [...mapped.price_list]
              : [...(variant?.price_list || [])],
          };
        });
        setProductVariants(reloadedVariants);
      } else if (isTableAutoMode) {
        showNotify('Bảng giá tự động chỉ hiển thị theo rule hiện có, không chỉnh sửa tại đây');
        return;
      }
      showNotify('Đã cập nhật');
    } finally {
      showLoading(false);
    }
  };

  const onVariantPriceListChange = (priceList, variantId) => {
    const i = productVariants.findIndex((x) => (x.id || x.variant_id) == variantId);
    if (i === -1) {
      return;
    }

    const newVariants = [...productVariants];
    newVariants[i].price_list = priceList;
    setProductVariants(newVariants);
    if (draftMode) {
      config?.onDraftVariantsChange?.(cloneVariantList(newVariants));
    } else {
      config?.onStateChange?.({
        variants: cloneVariantList(newVariants),
        selectedSourceKey,
        selectedTable,
        isTableSource,
        isTableManualMode,
        isTableAutoMode,
      });
    }
  };

  const onAllVariantPriceListChange = (priceList) => {
    const newVariants = [...productVariants].map((x) => ({
      ...x,
      price_list: priceList,
    }));
    setProductVariants(newVariants);
    if (draftMode) {
      config?.onDraftVariantsChange?.(cloneVariantList(newVariants));
    } else {
      config?.onStateChange?.({
        variants: cloneVariantList(newVariants),
        selectedSourceKey,
        selectedTable,
        isTableSource,
        isTableManualMode,
        isTableAutoMode,
      });
    }
  };

  const onVariantOriginalPriceChange = (originalPrice, variantId) => {
    const i = productVariants.findIndex((x) => (x.id || x.variant_id) == variantId);
    if (i === -1) {
      return;
    }

    const newVariants = [...productVariants];
    newVariants[i].original_price = originalPrice;
    setProductVariants(newVariants);
    if (draftMode) {
      config?.onDraftVariantsChange?.(cloneVariantList(newVariants));
    } else {
      config?.onStateChange?.({
        variants: cloneVariantList(newVariants),
        selectedSourceKey,
        selectedTable,
        isTableSource,
        isTableManualMode,
        isTableAutoMode,
      });
    }
  };

  const onAllVariantOriginalPriceChange = (originalPrice) => {
    const newVariants = [...productVariants].map((x) => ({
      ...x,
      original_price: originalPrice,
    }));

    setProductVariants(newVariants);
    if (draftMode) {
      config?.onDraftVariantsChange?.(cloneVariantList(newVariants));
    } else {
      config?.onStateChange?.({
        variants: cloneVariantList(newVariants),
        selectedSourceKey,
        selectedTable,
        isTableSource,
        isTableManualMode,
        isTableAutoMode,
      });
    }
  };

  return {
    id,
    product,
    productVariants,
    pricingEnabled,
    sourceOptions,
    selectedSourceKey,
    setSelectedSourceKey,
    selectedTable,
    isTableSource,
    isTableManualMode,
    isTableAutoMode,
    navigate,
    onUpdateProductVariantPriceList,
    onVariantPriceListChange,
    onAllVariantPriceListChange,
    onVariantOriginalPriceChange,
    onAllVariantOriginalPriceChange,
  };
};

export default useComponent;
